import React from 'react';

/**
 * Menu Item Skeleton Loader
 */
export const MenuItemSkeleton = () => {
  return (
    <div className="flex items-center bg-white rounded-xl shadow-sm border border-green-200 p-3 animate-pulse">
      <div className="flex-1 pr-3 space-y-2">
        <div className="h-5 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
      <div className="w-24 aspect-square rounded-2xl bg-gray-200"></div>
    </div>
  );
};

/**
 * Category Tab Skeleton Loader
 */
export const CategoryTabSkeleton = () => {
  return (
    <div className="flex gap-2 overflow-auto no-scrollbar py-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div 
          key={i} 
          className="h-8 bg-gray-200 rounded-md animate-pulse"
          style={{ width: `${60 + Math.random() * 40}px` }}
        ></div>
      ))}
    </div>
  );
};

/**
 * Bill Item Skeleton Loader
 */
export const BillItemSkeleton = () => (
  <div className="flex justify-between items-center py-2 animate-pulse">
    <div className="flex items-center gap-2 flex-1">
      <div className="w-8 h-5 bg-gray-200 rounded-full"></div>
      <div className="h-4 bg-gray-200 rounded w-32"></div>
    </div>
    <div className="h-4 bg-gray-200 rounded w-12"></div>
  </div>
);

/**
 * Full Bill Pay Skeleton Loader
 */
export const BillPaySkeleton = () => (
  <div className="w-full max-w-sm mx-auto bg-white rounded-2xl shadow-sm overflow-hidden pb-6 animate-pulse">
    {/* Header Skeleton */}
    <div className="border-b border-[#F4F4F4] px-4 py-5">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-200 rounded"></div>
        <div className="flex-1">
          <div className="h-5 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-20"></div>
        </div>
      </div>
    </div>

    {/* Items Skeleton */}
    <div className="my-3 border-b border-gray-200 px-4 py-3">
      <BillItemSkeleton />
      <BillItemSkeleton />
      <BillItemSkeleton />
    </div>

    {/* Total Skeleton */}
    <div className="px-4 mb-4">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-40"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded w-16"></div>
      </div>
    </div>

    {/* Tabs Skeleton */}
    <div className="px-4 mb-4">
      <div className="flex gap-3">
        <div className="flex-1 h-12 bg-gray-200 rounded-full"></div>
        <div className="flex-1 h-12 bg-gray-200 rounded-full"></div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="px-4">
      <div className="rounded-xl border border-gray-200 p-4 bg-slate-50/30">
        <div className="h-4 bg-gray-200 rounded w-48 mb-3"></div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-xl"></div>
          ))}
        </div>
        <div className="h-10 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  </div>
);

/**
 * Grid Menu Item Skeleton (if you need grid layout)
 */
export const GridMenuItemSkeleton = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4 animate-pulse">
      <div className="w-full aspect-square rounded-xl bg-gray-200 mb-3"></div>
      <div className="space-y-2">
        <div className="h-5 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  );
};

/**
 * Full Page Skeleton Loader
 */
export const MenuPageSkeleton = () => {
  return (
    <div className="w-full xls:max-w-sm bg-white rounded-2xl shadow-sm overflow-hidden pb-0 h-screen xls:mx-auto flex flex-col">
      {/* Header Skeleton */}
      <div className="border-b border-[#F4F4F4] flex-shrink-0">
        <div className="flex items-center justify-between gap-4 px-4 py-5">
          <div className="w-10 aspect-square bg-gray-200 rounded animate-pulse"></div>
          <div className="flex-1 h-11 bg-gray-200 rounded-xl animate-pulse"></div>
        </div>
      </div>

      {/* Categories Skeleton */}
      <div className="py-6 pl-4 flex-shrink-0">
        <CategoryTabSkeleton />
      </div>

      {/* Menu Items Skeleton */}
      <div className="px-4 space-y-3 py-4 overflow-auto flex-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <MenuItemSkeleton key={i} />
        ))}
      </div>
    </div>
  );
};


export const EmptyMenuState = ({ search, onClearSearch }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-4">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center mb-4">
        <i className="fa-solid fa-utensils text-green-600 text-3xl"></i>
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-2">
        {search ? "No Results Found" : "No Items Available"}
      </h3>
      <p className="text-sm text-gray-500 max-w-xs mb-4">
        {search 
          ? `We couldn't find any items matching "${search}"`
          : "No menu items available in this category at the moment"}
      </p>
      {search && onClearSearch && (
        <button
          onClick={onClearSearch}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
        >
          Clear Search
        </button>
      )}
    </div>
  );
};

/**
 * Empty State Component for Billing
 */
export const EmptyBillingState = ({ search, tableNumber }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-4">
      {search ? (
        // Loading state
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center mb-4">
          <i className="fa-solid fa-spinner fa-spin text-blue-600 text-3xl"></i>
        </div>
      ) : (
        // Empty state
        <>
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center mb-4">
            <i className="fa-solid fa-receipt text-orange-600 text-3xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No Active Bill
          </h3>
          <p className="text-sm text-gray-500 max-w-xs mb-4">
            {tableNumber 
              ? `No active bill found for Table ${tableNumber}`
              : "No active bill found"}
          </p>
          <a
            href="/bistro/menu"
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            <i className="fa-regular fa-utensils"></i>
            <span>View Menu</span>
          </a>
        </>
      )}
    </div>
  );
};

/**
 * Loading Spinner Component
 */
export const LoadingSpinner = ({ size = "md", className = "" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-16 h-16"
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`${sizeClasses[size]} border-4 border-green-200 border-t-green-600 rounded-full animate-spin`}></div>
    </div>
  );
};

export default {
  MenuItemSkeleton,
  CategoryTabSkeleton,
  GridMenuItemSkeleton,
  MenuPageSkeleton,
  EmptyMenuState,
  EmptyBillingState,
  BillItemSkeleton,
  BillPaySkeleton,
  LoadingSpinner
};